import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Modal from '@cloudscape-design/components/modal';
import Pagination from '@cloudscape-design/components/pagination';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import api, { resolveBackendAssetUrl } from '../services/apiService';
import { formatDateDe } from '../utils/date';

const statusLabels = {
  invited: ['pending', 'Neu'],
  viewed: ['in-progress', 'Link Geöffnet'],
  offered: ['success', 'Preisinformation gesendet'],
  selected: ['success', 'Ausgewählt'],
  not_selected: ['stopped', 'Anderes Preisangebot ausgewählt'],
  in_progress: ['in-progress', 'In Bearbeitung'],
  offer_sent: ['success', 'Angebot gesendet'],
  expired: ['stopped', 'Abgelaufen'],
};

const calculationBasisLabels = {
  rental_days: 'Miettage',
  calendar_days: 'Kalendertage',
};

const qualifiedOfferStatusOptions = [
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'offer_sent', label: 'Angebot gesendet' },
];

const requestStatusFilterOptions = [
  { value: 'all', label: 'Alle' },
  { value: 'invited', label: 'Neu' },
  { value: 'viewed', label: 'Link geöffnet' },
  { value: 'offered', label: 'Preisinformation gesendet' },
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'offer_sent', label: 'Angebot gesendet' },
  { value: 'not_selected', label: 'Anderes Preisangebot ausgewählt' },
  { value: 'expired', label: 'Abgelaufen' },
];

const pageSizeOptions = [
  { value: '10', label: '10 pro Seite' },
  { value: '25', label: '25 pro Seite' },
  { value: '50', label: '50 pro Seite' },
];

function formatCurrency(value, currency = 'EUR') {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(Number(value || 0));
}

function priceBindingLabel(hours) {
  return {
    12: '12 Stunden',
    24: '24 Stunden',
    48: '2 Tage',
    96: '4 Tage',
    168: '7 Tage',
    336: '14 Tage',
  }[Number(hours)] || '24 Stunden';
}

function priceBindingUntil(offer) {
  const submittedAt = new Date(offer?.submittedAt);
  if (Number.isNaN(submittedAt.getTime())) return '–';
  const until = new Date(submittedAt.getTime() + Number(offer?.priceBindingHours || 24) * 60 * 60_000);
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Berlin',
  }).format(until);
}

function requestLocation(request) {
  return [
    request?.streetAddress,
    `${request?.postalCode || ''} ${request?.city || ''}`.trim(),
  ].filter(Boolean).join(', ') || '–';
}

function requestMachines(request) {
  return request?.items?.map((position) => (
    `${position.quantity}× ${position.machine?.title || position.category?.name}`
  )).join(', ') || '–';
}

function effectiveInvitationStatus(invitation) {
  if (invitation.offer?.qualifiedAt) return invitation.offer.qualifiedOfferStatus || 'in_progress';
  return invitation.status;
}

function invitationSearchText(invitation) {
  const request = invitation.request || {};
  const machineTerms = (request.items || []).flatMap((item) => [
    item.machine?.manufacturer,
    item.machine?.model,
    item.machine?.title,
    item.category?.name,
    item.category?.familyName,
  ]);
  return [
    request.reference,
    request.streetAddress,
    request.postalCode,
    request.city,
    invitation.rentalStation?.name,
    invitation.rentalStation?.postalCode,
    invitation.rentalStation?.city,
    invitation.offer?.qualifiedCompanyName,
    invitation.offer?.qualifiedContactName,
    ...machineTerms,
  ].filter(Boolean).join(' ').toLocaleLowerCase('de-DE');
}

function lineRate(line, currency) {
  if (line.pricingMode === 'percent_of_rent') {
    return `${Number(line.rateValue).toLocaleString('de-DE', { maximumFractionDigits: 4 })} % vom Mietpreis`;
  }
  if (line.pricingMode === 'flat_per_day') {
    return `${formatCurrency(line.rateValue, currency)} pro Versicherungstag`;
  }
  return `${formatCurrency(line.unitPriceNet, currency)} ${line.unitLabel || ''}`.trim();
}

function RequestCard({ invitation, onOpen }) {
  const request = invitation.request;
  const offered = Boolean(invitation.offer);
  const qualified = Boolean(invitation.offer?.qualifiedAt);
  const otherOfferSelected = invitation.status === 'not_selected';
  const qualifiedOfferStatus = invitation.offer?.qualifiedOfferStatus || 'in_progress';
  const effectiveStatus = effectiveInvitationStatus(invitation);
  const status = statusLabels[effectiveStatus] || ['info', effectiveStatus];
  const station = invitation.rentalStation
    ? `${invitation.rentalStation.name} · ${Number(invitation.distanceKm).toLocaleString('de-DE', { maximumFractionDigits: 1 })} km`
    : '–';
  const content = (
    <>
      <div className="rental-request-card__header">
        <div>
          <strong className="rental-request-card__reference">{request?.reference || '–'}</strong>
          {(offered || otherOfferSelected) && (
            <span className={`rental-request-card__hint${qualified ? ' rental-request-card__hint--qualified' : ''}`}>
              {qualified
                ? qualifiedOfferStatus === 'offer_sent'
                  ? 'Anfrager hat Sie ausgewählt – Angebot bereits gesendet'
                  : 'Anfrager hat Sie ausgewählt – qualifiziertes Vermieterangebot senden'
                : otherOfferSelected
                  ? 'Der Anfrager hat sich für ein anderes Preisangebot entschieden'
                  : 'Preisangebotspositionen anzeigen'}
            </span>
          )}
        </div>
        <div className="rental-request-card__status">
          <StatusIndicator type={status[0]}>{status[1]}</StatusIndicator>
          <strong>{offered ? formatCurrency(invitation.offer.priceNet, invitation.offer.currency) : '–'}</strong>
          <small>Netto-Preisangebot</small>
        </div>
      </div>
      <div className="rental-request-card__facts">
        <div>
          <span>Zeitraum</span>
          <strong>{formatDateDe(request?.startDate)} – {formatDateDe(request?.endDate)}</strong>
          <small>{request?.rentalDays || '–'} Miettag{request?.rentalDays === 1 ? '' : 'e'}</small>
        </div>
        <div>
          <span>Einsatz / Transport</span>
          <strong>{requestLocation(request)}</strong>
          <small>{request?.transportMode === 'delivery_pickup' ? 'Anlieferung / Abholung' : 'Selbstabholer'}</small>
        </div>
        <div>
          <span>Mietstation</span>
          <strong>{station}</strong>
        </div>
        <div>
          <span>Bedarf</span>
          <strong>{requestMachines(request)}</strong>
        </div>
      </div>
    </>
  );

  if (offered) {
    return (
      <button
        type="button"
        className="rental-request-card rental-request-card--clickable"
        onClick={() => onOpen(invitation)}
        aria-label={`Preisangebotspositionen für Mietanfrage ${request?.reference || ''} anzeigen`}
      >
        {content}
      </button>
    );
  }
  return <article className="rental-request-card">{content}</article>;
}

function OfferDetails({ invitation, onDismiss, onOfferStatusChange }) {
  const offer = invitation?.offer;
  const request = invitation?.request;
  const [qualifiedOfferStatus, setQualifiedOfferStatus] = useState('in_progress');
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState('');
  const currency = offer?.currency || 'EUR';
  const lineItems = offer?.lineItems || [];
  const requestItems = new Map((request?.items || []).map((item) => [String(item.id), item]));
  const positionRows = lineItems.map((line) => {
    const requestItem = requestItems.get(String(line.requestItemId));
    return {
      ...line,
      imageUrl: requestItem?.machine?.imageUrl || null,
      details: line.lineType === 'operator_training' ? null : requestItem?.details || null,
    };
  });

  useEffect(() => {
    setQualifiedOfferStatus(offer?.qualifiedOfferStatus || 'in_progress');
    setStatusError('');
    setStatusSaving(false);
  }, [offer?.id, offer?.qualifiedOfferStatus]);

  async function changeQualifiedOfferStatus(status) {
    if (!offer?.id || status === qualifiedOfferStatus) return;
    const previousStatus = qualifiedOfferStatus;
    setQualifiedOfferStatus(status);
    setStatusSaving(true);
    setStatusError('');
    try {
      const response = await api.patch(`/tenant/offers/${offer.id}/status`, { status });
      const savedStatus = response.data.qualifiedOfferStatus;
      setQualifiedOfferStatus(savedStatus);
      onOfferStatusChange(offer.id, savedStatus);
    } catch (requestError) {
      setQualifiedOfferStatus(previousStatus);
      setStatusError(requestError.response?.data?.error || 'Der Status konnte nicht gespeichert werden.');
    } finally {
      setStatusSaving(false);
    }
  }

  return (
    <Modal
      visible={Boolean(invitation)}
      onDismiss={onDismiss}
      width={1500}
      position="top"
      header={`Preisangebotspositionen ${request?.reference || ''}`}
      footer={<Button onClick={onDismiss}>Schließen</Button>}
    >
      {invitation && (
        <SpaceBetween size="m">
          <div className="offer-detail-summary">
            <div><span>Preisangebot abgegeben</span><strong>{formatDateDe(offer?.submittedAt)}</strong></div>
            <div><span>Mietzeitraum</span><strong>{formatDateDe(request?.startDate)} – {formatDateDe(request?.endDate)}</strong></div>
            <div><span>Netto-Gesamtsumme</span><strong>{formatCurrency(offer?.priceNet, currency)}</strong></div>
            <div><span>Preisbindung</span><strong>{priceBindingLabel(offer?.priceBindingHours)}</strong></div>
            <div><span>Preise gebunden bis</span><strong>{priceBindingUntil(offer)}</strong></div>
          </div>

          {offer?.qualifiedAt ? (
            <SpaceBetween size="m">
              <Alert type="success" header="Der Anfrager hat sich für Sie entschieden">
                Ihre Preisinformation wurde ausgewählt. Bitte erstellen und senden Sie jetzt ein qualifiziertes Vermieterangebot auf Grundlage Ihrer hinterlegten Preisangaben an den unten genannten Kunden.
              </Alert>
              <div className="qualified-offer-status-control">
                <FormField
                  label="Status"
                  description="Bearbeitungsstand des qualifizierten Vermieterangebots"
                  errorText={statusError}
                >
                  <Select
                    options={qualifiedOfferStatusOptions}
                    selectedOption={qualifiedOfferStatusOptions.find((option) => option.value === qualifiedOfferStatus)}
                    onChange={({ detail }) => changeQualifiedOfferStatus(detail.selectedOption.value)}
                    disabled={statusSaving}
                    expandToViewport
                    ariaLabel="Status des qualifizierten Vermieterangebots"
                  />
                </FormField>
                {statusSaving && <small>Status wird gespeichert …</small>}
              </div>
              <section className="qualified-customer-panel">
                <Header
                  variant="h3"
                  description={`Vom Anfrager ausgewählt und freigegeben am ${formatDateDe(offer.qualifiedAt)}`}
                >
                  Kundendaten für das qualifizierte Vermieterangebot
                </Header>
                <div className="qualified-customer-grid">
                  <div><span>Unternehmen</span><strong>{offer.qualifiedCompanyName}</strong></div>
                  <div><span>Ansprechpartner</span><strong>{offer.qualifiedContactName}</strong></div>
                  <div><span>E-Mail</span><strong><a href={`mailto:${offer.qualifiedEmail}`}>{offer.qualifiedEmail}</a></strong></div>
                  <div><span>Telefon</span><strong><a href={`tel:${offer.qualifiedPhone}`}>{offer.qualifiedPhone}</a></strong></div>
                  <div>
                    <span>Firmenanschrift</span>
                    <strong>
                      {offer.qualifiedStreetAddress}<br />
                      {offer.qualifiedPostalCode} {offer.qualifiedCity} · {offer.qualifiedCountry}
                    </strong>
                  </div>
                </div>
              </section>
            </SpaceBetween>
          ) : invitation.status === 'not_selected' ? (
            <Alert type="info" header="Der Anfrager hat sich für ein anderes Preisangebot entschieden">
              Für diese Mietanfrage wird von Ihnen kein qualifiziertes Vermieterangebot mehr benötigt. Ihre abgegebene Preisinformation und die Preisangebotspositionen bleiben weiterhin einsehbar.
            </Alert>
          ) : (
            <Alert type="info">
              Die anonyme Preisinformation wurde noch nicht zu einem qualifizierten Vermieterangebot. Firmen- und Kontaktdaten werden erst nach der ausdrücklichen Freigabe angezeigt.
            </Alert>
          )}

          <Table
            wrapLines
            items={positionRows}
            header={<Header variant="h3">Preisangebotskalkulation</Header>}
            empty="Keine Positionsdaten vorhanden."
            columnDefinitions={[
              {
                id: 'thumbnail',
                header: 'Bild',
                width: 74,
                cell: (row) => row.lineType === 'operator_training'
                  ? <div className="offer-line-placeholder offer-line-placeholder--training" aria-label="Bedienerschulung">🎓</div>
                  : row.lineType === 'insurance'
                    ? <div className="offer-line-placeholder" aria-label="Versicherung"><span className="offer-line-insurance-shield" aria-hidden="true">✓</span></div>
                  : row.imageUrl
                    ? <img className="offer-line-thumbnail" src={resolveBackendAssetUrl(row.imageUrl)} alt={row.description} />
                    : (
                    <div
                      className={`offer-line-placeholder${row.lineType.includes('transport') ? ' offer-line-placeholder--transport' : ''}`}
                      aria-label={row.lineType === 'outbound_transport'
                        ? 'Hintransport'
                        : row.lineType === 'return_transport' ? 'Rücktransport' : 'SmartRent'}
                    >
                      {row.lineType === 'outbound_transport'
                        ? '→'
                        : row.lineType === 'return_transport' ? '←' : 'SR'}
                    </div>
                    ),
              },
              {
                id: 'article',
                header: 'Artikeltyp',
                width: 220,
                cell: (row) => (
                  <div className="offer-line-article">
                    <strong>{row.description}</strong>
                    {(row.quantity > 1 || row.lineType === 'operator_training') && (
                      <small>{row.lineType === 'operator_training' ? 'Teilnehmer' : 'Anzahl'}: {row.quantity}</small>
                    )}
                    {row.details && <small>{row.details}</small>}
                  </div>
                ),
              },
              {
                id: 'duration',
                header: 'Mietdauer',
                width: 150,
                cell: (row) => row.calculationBasis
                  ? `${row.durationDays} ${calculationBasisLabels[row.calculationBasis] || 'Tage'}`
                  : row.lineType === 'rental'
                    ? `${row.durationDays} Miettag${row.durationDays === 1 ? '' : 'e'}`
                    : row.lineType === 'operator_training' ? '1 Schulung' : '1 Fahrt',
              },
              { id: 'from', header: 'Von', width: 95, cell: (row) => formatDateDe(row.dateFrom) },
              { id: 'to', header: 'Bis', width: 95, cell: (row) => formatDateDe(row.dateTo) },
              {
                id: 'unitPrice',
                header: 'Satz / Einzelpreis netto',
                width: 210,
                cell: (row) => <strong>{lineRate(row, currency)}</strong>,
              },
              {
                id: 'total',
                header: 'Netto gesamt',
                width: 135,
                cell: (row) => <strong>{formatCurrency(row.totalNet, currency)}</strong>,
              },
            ]}
          />

          {offer?.message && (
            <div className="offer-detail-message">
              <span>Interner Hinweis zum Preisangebot</span>
              <p>{offer.message}</p>
            </div>
          )}
        </SpaceBetween>
      )}
    </Modal>
  );
}

export default function DashboardPage() {
  const [dashboard, setDashboard] = useState({
    openRequests: 0,
    submittedOffers: 0,
    receivedRequests: 0,
    receivedNetValue: 0,
    receivedNetCurrency: 'EUR',
    selectedCategories: 0,
  });
  const [invitations, setInvitations] = useState([]);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState(requestStatusFilterOptions[0]);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  function updateOfferStatus(offerId, qualifiedOfferStatus) {
    const updateInvitation = (invitation) => (
      invitation?.offer?.id === offerId
        ? { ...invitation, offer: { ...invitation.offer, qualifiedOfferStatus } }
        : invitation
    );
    setInvitations((current) => current.map(updateInvitation));
    setSelectedInvitation((current) => updateInvitation(current));
  }

  useEffect(() => {
    Promise.all([api.get('/tenant/dashboard'), api.get('/tenant/requests')])
      .then(([dashboardResponse, requestsResponse]) => {
        setDashboard(dashboardResponse.data);
        setInvitations(requestsResponse.data);
      })
      .catch(() => setError('Die Vermieterübersicht konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  const filteredInvitations = useMemo(() => {
    const normalizedSearch = filterText.trim().toLocaleLowerCase('de-DE');
    return invitations.filter((invitation) => {
      const matchesStatus = statusFilter.value === 'all'
        || effectiveInvitationStatus(invitation) === statusFilter.value;
      const matchesSearch = !normalizedSearch
        || invitationSearchText(invitation).includes(normalizedSearch);
      return matchesStatus && matchesSearch;
    });
  }, [filterText, invitations, statusFilter.value]);

  const pagesCount = Math.max(1, Math.ceil(filteredInvitations.length / pageSize));
  const paginatedInvitations = useMemo(() => {
    const offset = (currentPage - 1) * pageSize;
    return filteredInvitations.slice(offset, offset + pageSize);
  }, [currentPage, filteredInvitations, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, pageSize, statusFilter.value]);

  useEffect(() => {
    if (currentPage > pagesCount) setCurrentPage(pagesCount);
  }, [currentPage, pagesCount]);

  return (
    <>
      <Header variant="h1" description="Ihre passenden Mietanfragen und abgegebenen Preisangebote">Übersicht</Header>
      {error && <Alert type="error">{error}</Alert>}
      <div className="dashboard-metrics">
        <div className="metric"><span>Offene Anfragen</span><strong>{dashboard.openRequests}</strong></div>
        <div className="metric"><span>Abgegebene Preisangebote</span><strong>{dashboard.submittedOffers}</strong></div>
        <div className="metric metric--received">
          <span>Erhaltene Anfragen</span>
          <strong>{dashboard.receivedRequests}</strong>
          <small>{formatCurrency(dashboard.receivedNetValue, dashboard.receivedNetCurrency)} netto</small>
        </div>
        <div className="metric"><span>Hinterlegte Maschinentypen</span><strong>{dashboard.selectedCategories}</strong></div>
      </div>

      <section className="rental-requests-overview" aria-labelledby="rental-requests-heading">
        <div className="rental-requests-overview__header">
          <Header variant="h2" counter={`(${filteredInvitations.length} von ${invitations.length})`}>
            <span id="rental-requests-heading">Mietanfragen</span>
          </Header>
          <span>Anfragen mit abgegebenem Preisangebot können angeklickt werden.</span>
        </div>
        <div className="rental-requests-filters">
          <FormField label="Suche">
            <TextFilter
              filteringText={filterText}
              onChange={({ detail }) => setFilterText(detail.filteringText)}
              filteringPlaceholder="Referenz, Ort, Maschine oder Kunde suchen"
              filteringAriaLabel="Mietanfragen durchsuchen"
              countText={`${filteredInvitations.length} Treffer`}
            />
          </FormField>
          <FormField label="Status">
            <Select
              options={requestStatusFilterOptions}
              selectedOption={statusFilter}
              onChange={({ detail }) => setStatusFilter(detail.selectedOption)}
              expandToViewport
              ariaLabel="Mietanfragen nach Status filtern"
            />
          </FormField>
          <FormField label="Seitengröße">
            <Select
              options={pageSizeOptions}
              selectedOption={pageSizeOptions.find((option) => Number(option.value) === pageSize)}
              onChange={({ detail }) => setPageSize(Number(detail.selectedOption.value))}
              expandToViewport
              ariaLabel="Anzahl Mietanfragen pro Seite"
            />
          </FormField>
        </div>
        {loading ? (
          <div className="rental-requests-empty"><Spinner /> Anfragen werden geladen</div>
        ) : filteredInvitations.length > 0 ? (
          <>
            <div className="rental-request-list">
              {paginatedInvitations.map((invitation) => (
                <RequestCard key={invitation.id} invitation={invitation} onOpen={setSelectedInvitation} />
              ))}
            </div>
            <div className="rental-requests-pagination">
              <span>
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredInvitations.length)} von {filteredInvitations.length}
              </span>
              <Pagination
                currentPageIndex={currentPage}
                pagesCount={pagesCount}
                onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                ariaLabels={{
                  nextPageLabel: 'Nächste Seite',
                  previousPageLabel: 'Vorherige Seite',
                  pageLabel: (pageNumber) => `Seite ${pageNumber}`,
                }}
              />
            </div>
          </>
        ) : invitations.length > 0 ? (
          <div className="rental-requests-empty">Keine Mietanfragen entsprechen den gewählten Filtern.</div>
        ) : (
          <div className="rental-requests-empty">Noch keine passenden Anfragen vorhanden.</div>
        )}
      </section>

      <OfferDetails
        invitation={selectedInvitation}
        onDismiss={() => setSelectedInvitation(null)}
        onOfferStatusChange={updateOfferStatus}
      />
    </>
  );
}
